import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from '../../images/CloseIcon';
import { deleteSchedules, getSchedules } from '../../services/schedule.service';
import PlusIconWhite from '../../images/PlusIconWhite';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import { useResponsive } from '../../hook/useResponsive';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
  t,
} from '../../components/list';

interface ScheduleRow {
  id: string;
  applicableDays: string;
  scheduleName: string;
  validFrom: Date;
  validTo: Date;
  startTime: string;
  endTime: string;
}

interface InforTable {
  title: string;
  path: string;
  desc: string;
  titleCreate: string;
}
interface ScheduleResponse {
  data: {
    data: ScheduleRow[];
    count: number;
  };
}

type DayNumber = '1' | '2' | '3' | '4' | '5' | '6' | '7';

/** i18n keys of the day names, by the API's 1 (Monday) .. 7 (Sunday) codes. */
const dayMap: Record<DayNumber, string> = {
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
  '7': 'Sunday',
};

/** "1,2,3" -> "Monday,Tuesday,Wednesday" (translated). */
const getDayNamesFromApplicableDays = (applicableDaysStr: string, language: any) =>
  String(applicableDaysStr ?? '')
    .split(',')
    .map(num => dayMap[num.trim() as DayNumber])
    .filter(Boolean)
    .map(name => t(language, name, name))
    .join(',');

/** yyyy-mm-dd, the format this screen has always used (unlike the dd.mm.yyyy elsewhere). */
const formatDate = (dateString: Date) => {
  const d = new Date(dateString);
  if (!dateString || Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Schedules = () => {
  const preLanguage = localStorage.getItem('language');
  const { isMobile } = useResponsive();
  const [language, setLanguage] = useState<any>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>('');
  const inforTable: InforTable = {
    title:
      language && language?.['No schedule yet'] ? language?.['No schedule yet'] : 'No schedule yet',
    path: '/schedules/new',
    desc:
      language &&
        language?.['Create schedule to manage permissions and access for devices within the system.']
        ? language?.[
        'Create schedule to manage permissions and access for devices within the system.'
        ]
        : 'Create schedule to manage permissions and access for devices within the system.',
    titleCreate: language && language?.['New Role'] ? language?.['New Role'] : 'New Role',
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /** One page of schedules; the search box maps onto the endpoint's `searchValue`. */
  const fetchSchedules = async (query: ListQuery) => {
    webLoadingFalse();
    const response: ScheduleResponse = await getSchedules({
      page: query.page,
      limit: query.limit,
      searchValue: query.search,
    });
    return {
      rows: response.data.data ?? [],
      count: response.data.count ?? 0,
    };
  };

  const goToSchedule = (id: string) => webNavigate('/' + preLanguage + '/schedules/' + id);

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };
  const handleDeleteEmployee = async () => {
    try {
      const response = await deleteSchedules(idDelete);
      showDeleteToast(language?.['Schedule Deleted !'] ?? 'Schedule Deleted !');
    } catch (error) {
      console.error('Error loading terms:', error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<ScheduleRow>[] = [
    {
      key: 'scheduleName',
      label: 'Schedule name',
      width: 200,
      minWidth: 120,
      hideable: false,
      render: cells.primary(s => s.scheduleName),
      mobile: 'title',
      onClick: s => goToSchedule(String(s.id)),
    },
    {
      key: 'validFrom',
      label: 'Start date',
      width: 110,
      minWidth: 84,
      value: s => formatDate(s.validFrom),
      mobile: 'subtitle',
    },
    {
      key: 'validTo',
      label: 'End date',
      width: 110,
      minWidth: 84,
      value: s => formatDate(s.validTo),
      mobile: 'subtitle',
    },
    { key: 'startTime', label: 'Start time', width: 90, mobile: 'trailing' },
    { key: 'endTime', label: 'End time', width: 90, mobile: 'trailing' },
    {
      key: 'applicableDays',
      label: 'Days available',
      width: 240,
      minWidth: 140,
      value: (s, ctx) => getDayNamesFromApplicableDays(s.applicableDays, ctx.language),
      mobile: 'sheet',
    },
  ];

  const rowActions: ListRowAction<ScheduleRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIconV2 />,
      sheetIcon: 'inventory',
      onRun: s => goToSchedule(String(s.id)),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIconV2 />,
      sheetIcon: 'close',
      danger: true,
      onRun: s => handleShowDelete(String(s.id)),
    },
  ];

  return (
    <>
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? "auto" : undefined,
          paddingBottom: isMobile ? "24px" : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        <div className="lg:flex items-center justify-between" >
          <p
            style={{ color: '#222525' }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language && language?.['Schedules'] ? language?.['Schedules'] : 'Schedules'}
          </p>
          <div
            className="cursor-pointer flex items-center justify-center"
            style={{
              gap: '8px',
              padding: "8px 24px 8px 16px",
              backgroundColor: '#0F7679',
              borderRadius: '30px',
              width: "fit-content", marginTop: isMobile ? "20px" : '0px',
            }}
            onClick={() => {
              webNavigate('/' + preLanguage + '/schedules/new');
            }}
          >
            <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
              <PlusIconWhite />
            </div>
            <p
              style={{ color: '#FFFFFF' }}
              className="text-[14px] leading-[130%] font-medium font-inter"
            >
              {language && language?.['Add schedule'] ? language?.['Add schedule'] : 'Add schedule'}
            </p>
          </div>
        </div>
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchSchedules}
          searchPlaceholder="Search schedules"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="schedules"
          emptyState={inforTable}
        />
      </div>
      <div
        className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100vw", height: "100vh", left: 0, top: 0 }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            padding: '24px',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            width: '530px',
            maxWidth: 'calc(100vw - 32px)',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div className="flex items-center justify-between">
            <p
              style={{ fontSize: '20px', color: '#222525' }}
              className="font-semibold font-inter leading-[24px]"
            >
              {language && language?.['Delete schedule?']
                ? language?.['Delete schedule?']
                : 'Delete schedule?'}
            </p>
            <div
              onClick={() => setShowDelete(false)}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
            <p
              className="text-[14px] font-inter leading-[24px]"
              style={{ color: '#6A7878' }}
            >
              {language &&
                language?.[
                'This schedule will be permanently deleted. Are you sure you want to continue'
                ]
                ? language?.[
                'This schedule will be permanently deleted. Are you sure you want to continue'
                ]
                : 'This schedule will be permanently deleted. Are you sure you want to continue'}
            </p>
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => setShowDelete(false)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </div>
            <div
              onClick={handleDeleteEmployee}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Schedules;
